import React, { useContext, useEffect } from "react"
import { observer } from "mobx-react"
import { Anchor, Button, Group, Paper, Stack, Text, Title } from "@mantine/core"
import { CodeHighlight } from "@mantine/code-highlight"
import {
  IconArrowRight,
  IconBook,
  IconBrandGithub,
  IconBrandWordpress,
  IconExternalLink,
  IconCode,
  IconMarkdown,
  IconPlayerPlay,
  IconSettings
} from "@tabler/icons-react"
import { AppWorkspaceContext } from "../../stores/AppWorkspace"
import { agentDirectionsFor, frameworkHeading, FRAMEWORKS, LANDING_COPY, relatedLinks } from "../../../docs/catalog"
import { guideBySlug, guidesChunk } from "../../../docs/lazy"
import type { Framework } from "../../../docs/types"
import type { LandingFaq, LandingFrameworkCopy, LandingPluginSetup } from "../../../docs/landing"
import { GITHUB_URL, integrationPath, NWC_PATH, SWAP_PROVIDER_PATH } from "../../helpers/constants"
import { CopyAgentDirectionsButtons } from "./CopyAgentDirectionsButtons"
import { useNearViewport } from "../../hooks/useNearViewport"
import { IntegrationTypeBadge } from "./IntegrationTypeBadge"
import { CapabilityFacts } from "./CapabilityFacts"
import { AGENT_HASH, inline, NoCodeCheckout, NoCodeSetup, WP_ADMIN_HASH } from "./NoCodeSetup"
import { codeLanguage } from "../../../highlight"
import { MarkdownDoc } from "../MarkdownDoc"
import { EnvVarPlatforms } from "../setup/EnvVarPlatforms"
import { BTCPAY_SLUG, WORDPRESS_SLUG } from "../../helpers/constants"
import { startShopCable } from "../../shop/cable"
import { TrackedDemoShop } from "../TrackedDemoShop"
import { TestedBadge } from "../platforms/TestedBadge"
import { SiteVideo } from "../SiteVideo"
import { MobileHandoff } from "../MobileHandoff"
import { frameworkVideo } from "../../helpers/siteVideo"
import {
  captureQuickstartClick,
  type CopyCtaLocation,
  type QuickstartCtaLocation
} from "../../../analytics"

// A framework landing page, /integrations/<id>: the page a visitor lands on
// from a search result, a tutorial or a video, written to work with no prior
// visit to the homepage. Every FACT on it — heading, install line, version
// floor, the code it quotes, the example and payload it links — comes from
// the contract's frameworks[] row (docs/generated/frameworks.json). The copy
// around those facts is config/landing_copy.json, which the Rails side also
// reads for the server-rendered body, so a crawler and a browser get one page.
//
// Section order is the spec's: hero (with the page's video beside the copy) →
// live checkout → benefits → setup → practical questions → next step. A store
// plugin with a no-code setup (WooCommerce) moves the setup up to second,
// because that visitor came to install, and the setup leads with the store's
// own admin screens; the agent directions are its second tab.
//
// A recipe row (contract v9) has no example app: its page offers the recipe
// wherever another page offers the quickstart or the example, and its setup
// ends by pointing at the recipe. Since v10 a recipe may have agent
// directions, and then it gets the copy button like any other row; without
// them the recipe takes the copy button's place too.

const DEMO_HASH = "demo"

// What the phone-only handoff line tells a visitor to search for, where the
// row's label is not what they would type, and a shorter short link where
// config/routes.rb has one.
const HANDOFF_NAMES: Record<string, string> = {
  woocommerce: "WooCommerce",
  "easy-digital-downloads": "Easy Digital Downloads"
}
const HANDOFF_PATHS: Record<string, string> = { "easy-digital-downloads": "/edd" }

const fill = (template: string, framework: Framework) => template.replace(/\{label\}/g, framework.label)

const viaBtcpay = (framework: Framework) => framework.type === "via-btcpay"

// A via-BTCPay store answers from the BTCPay-route copy first: its own copy
// describes the native integration it may get later.
const faqFor = (framework: Framework, copy: LandingFrameworkCopy | undefined): LandingFaq[] =>
  LANDING_COPY.shared.faq.map((entry) => {
    const routed = viaBtcpay(framework) ? LANDING_COPY.integrations.viaBtcpay.faqOverrides[entry.q] : undefined
    const answer = routed ?? copy?.faqOverrides?.[entry.q] ?? entry
    return { ...answer, q: fill(answer.q, framework), a: fill(answer.a, framework) }
  })

// The setup overview's own steps for a plugin page (every plugin row but
// WooCommerce's). A plugin row without copy of its own gets a neutral pair.
const pluginSetupFor = (framework: Framework, copy: LandingFrameworkCopy | undefined): LandingPluginSetup =>
  (viaBtcpay(framework) ? LANDING_COPY.integrations.viaBtcpay.setup : copy?.setup) ?? {
    title: "Four steps",
    steps: [
      { title: "Install the plugin", install: true },
      {
        title: "Connect the wallet",
        body: "Save the NWC code where the {label} quickstart says, and add the LSC code to switch swaps on."
      }
    ]
  }

// "Example app" for a code integration; for a plugin, its source unless the
// row points at a demo shop under examples/.
const exampleLabel = (framework: Framework): string =>
  framework.sharedCheckoutDemo
    ? "Example app"
    : framework.exampleUrl?.includes("/examples/")
      ? "Example shop"
      : "Plugin source"

export const IntegrationPage: React.FC = observer(() => {
  const workspace = useContext(AppWorkspaceContext)
  const framework = workspace.framework

  // Same transport the homepage plugs in: the shared checkout below is the
  // real shop, polling plus push, for as long as this page is mounted.
  useEffect(() => startShopCable(workspace.shop), [workspace.shop])

  if (!framework) return <UnknownFramework id={workspace.frameworkId} />
  return <FrameworkLanding framework={framework} />
})

// The server answered 404 for this id already; this is only what the SPA
// draws if it navigated here client-side with a stale link.
const UnknownFramework: React.FC<{ id: string | null }> = observer(({ id }) => {
  const workspace = useContext(AppWorkspaceContext)
  return (
    <Stack gap="md" py="xl">
      <Title order={1}>No framework page for “{id}”</Title>
      <Text c="dimmed">These are the frameworks OpenReceive has a page for:</Text>
      <Group gap="sm">
        {FRAMEWORKS.map((framework) => (
          <Button
            key={framework.id}
            component="a"
            variant="default"
            href={integrationPath(framework.id)}
            onClick={(event) => {
              event.preventDefault()
              workspace.goIntegration(framework.id)
            }}
          >
            {framework.label}
          </Button>
        ))}
      </Group>
    </Stack>
  )
})

const FrameworkLanding: React.FC<{ framework: Framework }> = observer(({ framework }) => {
  const workspace = useContext(AppWorkspaceContext)
  const copyText = LANDING_COPY.frameworks[framework.id]
  const directions = framework.agentStack ? agentDirectionsFor(framework.agentStack) : null
  const recipe = framework.type === "recipe"
  const benefits = copyText?.benefits ?? LANDING_COPY.shared.benefits
  const faq = faqFor(framework, copyText)
  const noCode = copyText?.noCodeSetup
  const checkoutDemo = showsCheckoutDemo(framework, Boolean(noCode))
  const pluginSetup = pluginSetupFor(framework, copyText)

  const copyButtons = (
    size: "md" | "lg",
    ctaLocation: CopyCtaLocation,
    variant: "filled" | "default" = "filled"
  ) =>
    framework.agentStack ? (
      <CopyAgentDirectionsButtons
        stack={framework.agentStack}
        framework={framework.id}
        ctaLocation={ctaLocation}
        size={size}
        variant={variant}
        className="or-hero-cta-primary"
        preload
      />
    ) : null

  // The caption under the copy buttons, with the two places the same bytes can
  // be read first: as a file, and as a page.
  const copyCaption = directions ? (
    <Text className="or-integration-copy-caption" size="sm" c="dimmed">
      {fill(
        viaBtcpay(framework) ? LANDING_COPY.integrations.viaBtcpay.copyCaption : LANDING_COPY.shared.copyCaption,
        framework
      )}{" "}
      <Anchor href={directions.path} target="_blank" rel="noopener noreferrer">
        <IconMarkdown size={14} aria-hidden="true" /> raw markdown
      </Anchor>
      {directions.guidePath ? (
        <>
          {" · "}
          <Anchor
            href={directions.guidePath}
            onClick={(event) => {
              event.preventDefault()
              captureQuickstartClick({
                framework: framework.id,
                destinationPath: directions.guidePath as string,
                ctaLocation: "integration_copy_caption"
              })
              workspace.openHref(directions.guidePath as string)
            }}
          >
            readable page
          </Anchor>
        </>
      ) : null}
      {" · "}
      {(directions.bytes / 1024).toFixed(1)} KB
    </Text>
  ) : null

  // Every row has a /<id> short link (pages_controller_test checks it).
  const handoff = (
    <MobileHandoff
      name={HANDOFF_NAMES[framework.id] ?? framework.label}
      shortPath={HANDOFF_PATHS[framework.id] ?? `/${framework.id}`}
      pageKind="integration"
      pageId={framework.id}
    />
  )

  const related = relatedLinks(framework, workspace.learnPages)

  // A recipe's guide is the recipe, so every guide button says so.
  const recipeLabel = fill(LANDING_COPY.integrations.recipeLink, framework)

  const quickstartLink = (
    label: string,
    ctaLocation: QuickstartCtaLocation,
    variant: "filled" | "default" = "default"
  ) => (
    <Button
      component="a"
      href={framework.quickstartPath}
      variant={variant}
      size="md"
      className={variant === "filled" ? "or-hero-cta-primary" : undefined}
      leftSection={<IconBook size={18} />}
      onClick={(event) => {
        event.preventDefault()
        captureQuickstartClick({
          framework: framework.id,
          destinationPath: framework.quickstartPath,
          ctaLocation
        })
        workspace.openHref(framework.quickstartPath)
      }}
    >
      {recipe ? recipeLabel : label}
    </Button>
  )

  const exampleLink = framework.exampleUrl ? (
    <Button
      component="a"
      href={framework.exampleUrl}
      variant="default"
      size="md"
      target="_blank"
      rel="noopener noreferrer"
      leftSection={<IconBrandGithub size={18} />}
      rightSection={<IconExternalLink size={14} />}
    >
      {exampleLabel(framework)}
    </Button>
  ) : null

  // The plugin's listing in the WordPress.org directory, where a store owner
  // installs it from; beside the source wherever the source is offered.
  const directoryLink = copyText?.pluginDirectory ? (
    <Button
      component="a"
      href={copyText.pluginDirectory.href}
      variant="default"
      size="md"
      target="_blank"
      rel="noopener noreferrer"
      leftSection={<IconBrandWordpress size={18} />}
      rightSection={<IconExternalLink size={14} />}
    >
      {copyText.pluginDirectory.label}
    </Button>
  ) : null

  return (
    <Stack gap={48} className="or-integration" pb="xl">
      {/* 1. Hero */}
      <section className="or-integration-hero">
        <div className="or-integration-hero-copy">
          {workspace.frameworkLogoUrl(framework.id) ? (
            <span className="or-framework-logo or-integration-logo" data-framework={framework.id}>
              <img src={workspace.frameworkLogoUrl(framework.id)} alt={`${framework.label} logo`} />
            </span>
          ) : null}
          <Text className="or-eyebrow">OpenReceive for {framework.label}</Text>
          <Title order={1} className="or-integration-title">
            {frameworkHeading(framework)}
          </Title>
          <Text className="or-lede or-integration-lede">{copyText?.lede ?? ""}</Text>
          <div className="or-integration-badges">
            <IntegrationTypeBadge type={framework.type} />
            <TestedBadge tested={framework.tested} />
          </div>
          {noCode ? (
            <>
              <Group className="or-hero-actions" gap="sm" mt="lg" align="flex-end">
                <Button
                  component="a"
                  href={`#${WP_ADMIN_HASH}`}
                  size="md"
                  className="or-hero-cta-primary"
                  leftSection={<IconSettings size={18} />}
                  onClick={(event) => {
                    event.preventDefault()
                    workspace.setHash(WP_ADMIN_HASH, true)
                  }}
                >
                  {noCode.heroCta}
                </Button>
                <Button
                  component="a"
                  href={`#${AGENT_HASH}`}
                  variant="default"
                  size="md"
                  leftSection={<IconCode size={18} />}
                  onClick={(event) => {
                    event.preventDefault()
                    workspace.setHash(AGENT_HASH, true)
                  }}
                >
                  {noCode.agentCta}
                </Button>
              </Group>
              <Text className="or-integration-copy-caption" size="sm" c="dimmed">
                {noCode.heroCaption}
              </Text>
              {handoff}
            </>
          ) : (
            <>
          <Group className="or-hero-actions" gap="sm" mt="lg" align="flex-end">
            {copyButtons("md", "integration_hero") ??
              quickstartLink(recipeLabel, "integration_hero", "filled")}
            <Button
              component="a"
              href={`#${DEMO_HASH}`}
              variant="default"
              size="md"
              leftSection={<IconPlayerPlay size={18} />}
              onClick={(event) => {
                event.preventDefault()
                workspace.setHash(DEMO_HASH, true)
              }}
            >
              {checkoutDemo
                ? "Try the live demo"
                : framework.video
                  ? "Watch the demo"
                  : "See the setup"}
            </Button>
          </Group>
          {copyCaption}
          {handoff}
            </>
          )}
        </div>
        {/* The video where the mascot was (the user, 2026-10-20): the
            platform's own cut, else the explainer. Click to play only. */}
        <IntegrationVideo framework={framework} />
      </section>

      {noCode ? (
        <NoCodeSetup
          framework={framework}
          setup={noCode}
          agentActions={
            <>
              {copyButtons("md", "integration_setup")}
              {copyCaption}
            </>
          }
          footer={
            <Group gap="sm" mt="xl">
              {quickstartLink(`Full ${framework.label} quickstart`, "integration_setup")}
              {directoryLink}
              {exampleLink}
            </Group>
          }
        />
      ) : null}

      {/* 2. Live checkout — or, for BTCPay and WooCommerce, the plugin's own video and walkthrough */}
      <section id={DEMO_HASH} className="or-integration-demo">
        {checkoutDemo ? (
          <>
            <Title order={2} className="or-demo-title">
              Try the checkout your customers will see
            </Title>
            <Text className="or-demo-lede">
              {fill(copyText?.demoNote ?? LANDING_COPY.shared.demoNote, framework)}
            </Text>
            <TrackedDemoShop
              shop={workspace.shop}
              sourceLocation="integration_demo"
            />
          </>
        ) : noCode ? (
          <NoCodeCheckout setup={noCode} />
        ) : (
          <PluginDemo framework={framework} />
        )}
      </section>

      {/* 3. Benefits */}
      <section className="or-integration-benefits" aria-labelledby="or-benefits-title">
        <Title order={2} id="or-benefits-title" className="or-demo-title">
          Why OpenReceive
        </Title>
        <ul className="or-benefit-cards">
          {benefits.map((benefit) => (
            <li key={benefit.title} className="or-benefit-card">
              <Text className="or-benefit-title">{benefit.title}</Text>
              <Text className="or-benefit-body">{inline(benefit.body)}</Text>
            </li>
          ))}
        </ul>
      </section>

      {/* 5. Setup overview — a no-code page drew its own above */}
      {noCode ? null : (
      <Paper className="or-integration-setup" radius="lg" p={{ base: "md", sm: "xl" }} withBorder>
        <Text className="or-eyebrow">Set up</Text>
        <Title order={2} className="or-quicksetup-title">
          {recipe
            ? "Five steps, real code"
            : framework.sharedCheckoutDemo
              ? "Six steps, real code"
              : fill(pluginSetup.title, framework)}
        </Title>
        {framework.requires ? (
          <Text className="or-integration-requires">
            Requires {framework.requires}.
            {pluginSetup.requiresHref && !framework.sharedCheckoutDemo && !recipe ? (
              <>
                {" "}
                <Anchor
                  href={pluginSetup.requiresHref}
                  className="or-nowrap"
                  onClick={(event) => {
                    event.preventDefault()
                    workspace.openHref(pluginSetup.requiresHref as string)
                  }}
                >
                  {pluginSetup.requiresLabel} <IconArrowRight size={14} aria-hidden="true" />
                </Anchor>
              </>
            ) : null}
          </Text>
        ) : null}

        {/* The credentials first, then the stack's own steps. Numbered in
            one run, because a visitor does them in this order; the BTCPay
            plugin has no environment variables (its settings live in the
            store UI), so its run is shorter. */}
        <Stack gap="lg" mt="lg">
          <div className="or-quicksetup-step">
            <Text className="or-quicksetup-step-title">1. Get a receive-only NWC code</Text>
            <Text className="or-quicksetup-step-body">
              An NWC code is a receive-only connection string to a wallet you control. OpenReceive
              uses it to issue invoices and watch for payment, and it can never spend from your
              wallet.
            </Text>
            <Button component="a" href={NWC_PATH} size="sm" mt="sm">
              Get a receive-only NWC code
            </Button>
          </div>

          <div className="or-quicksetup-step">
            <Text className="or-quicksetup-step-title">
              2. Get an LSC code for swaps{" "}
              <Text span className="or-quicksetup-optional">
                (optional)
              </Text>
            </Text>
            <Text className="or-quicksetup-step-body">
              Only if you want customers to pay in currencies other than Bitcoin. A swap provider
              takes their USDT, USDC, ETH or SOL and settles it to you in Bitcoin.
            </Text>
            <Button component="a" href={SWAP_PROVIDER_PATH} size="sm" variant="default" mt="sm">
              Set up a swap provider
            </Button>
          </div>

          {framework.sharedCheckoutDemo ? (
            <div className="or-quicksetup-step or-quicksetup-step-wide">
              <Text className="or-quicksetup-step-title">3. Set the environment variables</Text>
              <Text className="or-quicksetup-step-body" mb="md">
                OpenReceive reads both codes from the environment of your {framework.label} server
                process.
              </Text>
              <EnvVarPlatforms />
            </div>
          ) : null}

          {framework.sharedCheckoutDemo ? (
            <div className="or-quicksetup-step">
              <Text className="or-quicksetup-step-title">4. Install</Text>
              <CodeHighlight
                className="or-integration-code"
                code={framework.install}
                language="bash"
                withCopyButton
                radius="md"
              />
            </div>
          ) : null}

          {framework.snippets.wire ? (
            <div className="or-quicksetup-step or-quicksetup-step-wide">
              <Text className="or-quicksetup-step-title">5. Wire OpenReceive</Text>
              <Text className="or-quicksetup-step-body" mb="sm">
                Your hooks decide who may pay and how much; OpenReceive supplies invoices, polling,
                settlement and the checkout UI. Quoted from the {framework.label} quickstart.
              </Text>
              <CodeHighlight
                className="or-integration-code"
                code={framework.snippets.wire.code}
                language={codeLanguage(framework.snippets.wire.language)}
                withCopyButton
                radius="md"
              />
            </div>
          ) : recipe ? (
            <div className="or-quicksetup-step">
              <Text className="or-quicksetup-step-title">5. Follow the recipe</Text>
              <Text className="or-quicksetup-step-body">{LANDING_COPY.integrations.recipeStep}</Text>
            </div>
          ) : framework.sharedCheckoutDemo ? null : (
            pluginSetup.steps.map((step, index) => (
              <div className="or-quicksetup-step" key={step.title}>
                <Text className="or-quicksetup-step-title">
                  {index + 3}. {fill(step.title, framework)}
                </Text>
                {step.install ? (
                  <CodeHighlight
                    className="or-integration-code"
                    code={framework.install}
                    language="text"
                    withCopyButton={false}
                    radius="md"
                  />
                ) : (
                  <Text className="or-quicksetup-step-body">{inline(fill(step.body ?? "", framework))}</Text>
                )}
              </div>
            ))
          )}

          {framework.snippets.render ? (
            <div className="or-quicksetup-step or-quicksetup-step-wide">
              <Text className="or-quicksetup-step-title">6. Render the checkout</Text>
              <CodeHighlight
                className="or-integration-code"
                code={framework.snippets.render.code}
                language={codeLanguage(framework.snippets.render.language)}
                withCopyButton
                radius="md"
              />
            </div>
          ) : null}
        </Stack>

        <Group gap="sm" mt="xl">
          {quickstartLink(`Full ${framework.label} quickstart`, "integration_setup")}
          {directoryLink}
          {exampleLink}
        </Group>
      </Paper>
      )}

      {/* 5b. How it runs: the row's capability record (contract v9) */}
      <CapabilityFacts capabilities={framework.capabilities} />

      {/* 6. Practical questions */}
      <section className="or-integration-faq" aria-labelledby="or-faq-title">
        <Title order={2} id="or-faq-title" className="or-demo-title">
          Practical questions
        </Title>
        <dl className="or-faq">
          {faq.map((entry) => (
            <div key={entry.q} className="or-faq-item">
              <dt>{entry.q}</dt>
              <dd>
                {entry.a}
                {entry.href ? (
                  <>
                    {" "}
                    <Anchor
                      href={entry.href}
                      onClick={(event) => {
                        if (!entry.href?.startsWith("/guides")) return
                        event.preventDefault()
                        captureQuickstartClick({
                          framework: framework.id,
                          destinationPath: entry.href,
                          ctaLocation: "integration_faq"
                        })
                        workspace.openHref(entry.href)
                      }}
                    >
                      {entry.label ?? "Read more"} <IconArrowRight size={14} aria-hidden="true" />
                    </Anchor>
                  </>
                ) : null}
              </dd>
            </div>
          ))}
        </dl>
      </section>

      {/* 7. Next step */}
      <section className="or-integration-next">
        <Title order={2} className="or-demo-title">
          {framework.sharedCheckoutDemo
            ? `Add OpenReceive to your ${framework.label} app`
            : `Add OpenReceive to ${framework.label}`}
        </Title>
        <Group className="or-hero-actions" gap="sm" mt="md" align="flex-end">
          {noCode ? (
            <Button
              component="a"
              href={`#${WP_ADMIN_HASH}`}
              size="lg"
              leftSection={<IconSettings size={20} />}
              onClick={(event) => {
                event.preventDefault()
                workspace.setHash(WP_ADMIN_HASH, true)
              }}
            >
              {noCode.heroCta}
            </Button>
          ) : null}
          {copyButtons("lg", "integration_next_step", noCode ? "default" : "filled")}
          {quickstartLink("Full quickstart", "integration_next_step", recipe ? "filled" : "default")}
          {directoryLink}
          {exampleLink}
          <Button
            component="a"
            href="/api_docs"
            variant="default"
            size="md"
            onClick={(event) => {
              event.preventDefault()
              workspace.goApiDocs()
            }}
          >
            API reference
          </Button>
          <Button
            component="a"
            href={GITHUB_URL}
            variant="default"
            size="md"
            target="_blank"
            rel="noopener noreferrer"
            leftSection={<IconBrandGithub size={18} />}
          >
            GitHub
          </Button>
        </Group>
        {copyCaption}
      </section>

      {/* 8. Related: the platforms that run on this framework, the framework
          pages it is paired with and the /learn articles it names (relatedLinks). */}
      {related.length ? (
        <section className="or-integration-related">
          <Title order={2} className="or-demo-title">
            {LANDING_COPY.integrations.relatedTitle}
          </Title>
          <Group gap="sm" mt="sm">
            {related.map((link) => (
              <Button
                key={link.href}
                component="a"
                href={link.href}
                variant="default"
                size="sm"
                onClick={(event) => {
                  // A /learn article is a server page: let the browser load it.
                  if (link.platformId) {
                    event.preventDefault()
                    workspace.goPlatforms(link.platformId)
                  } else if (link.frameworkId) {
                    event.preventDefault()
                    workspace.goIntegration(link.frameworkId)
                  }
                }}
              >
                {link.label}
              </Button>
            ))}
          </Group>
        </section>
      ) : null}
    </Stack>
  )
})

// The README that stands in for a live demo: WooCommerce's own checkout, and
// BTCPay Server's for BTCPay itself and the stores that send buyers to it. Every
// other store plugin (Paymenter, FOSSBilling, Medusa, …) shows the OpenReceive
// checkout itself, so its page gets the live demo instead.
const pluginOverviewSlug = (framework: Framework): string | null =>
  framework.id === "woocommerce"
    ? WORDPRESS_SLUG
    : framework.id === "btcpay-server" || framework.type === "via-btcpay"
      ? BTCPAY_SLUG
      : null

// The live checkout demo: every code integration, and every store plugin whose
// buyers pay in the OpenReceive checkout rather than in BTCPay's or WooCommerce's.
const showsCheckoutDemo = (framework: Framework, noCode: boolean): boolean =>
  framework.sharedCheckoutDemo || (!noCode && pluginOverviewSlug(framework) === null)

// The plugin's published README (/btcpay or /wordpress), including its contract
// media, stands in for the live demo. Only the H1 is dropped because this
// section has its own heading.
const IntegrationVideo: React.FC<{ framework: Framework }> = ({ framework }) => {
  const video = frameworkVideo(framework.id, viaBtcpay(framework))
  const caption =
    video === "explainer"
      ? "What OpenReceive does, in one video"
      : video === "wpplugin"
        ? "Setting up the OpenReceive plugin in WordPress"
        : video === "btcpay"
          ? "The OpenReceive plugin for BTCPay Server"
          : `An AI coding agent adding payments to a ${framework.label} app`
  return (
    <SiteVideo
      video={video}
      caption={caption}
      label={video === "explainer" ? "What OpenReceive is and how it works" : caption}
      sourceLocation="integration"
      className="or-hero-video or-integration-hero-video"
    />
  )
}

// An observer: the README comes from the chunk carrying every guide's
// markdown, fetched as the reader nears this section and drawn when it lands.
const PluginDemo: React.FC<{ framework: Framework }> = observer(({ framework }) => {
  const wordpress = framework.id === "woocommerce"
  const [ref, near] = useNearViewport<HTMLDivElement>()
  const readme = near ? guideBySlug(pluginOverviewSlug(framework) ?? BTCPAY_SLUG) : null
  const walkthrough = readme ? readme.markdown.replace(/^# [^\n]+\n+/, "") : null
  return (
    <>
      <Title order={2} className="or-demo-title">
        {wordpress ? "See the WooCommerce checkout" : "See the plugin at work"}
      </Title>
      <Text className="or-demo-lede">
        {wordpress
          ? "WooCommerce owns the order and checkout, so this is the plugin's own setup and payment flow."
          : framework.type === "via-btcpay"
            ? `${framework.label} sends the buyer to BTCPay Server's checkout, so this is the OpenReceive plugin for BTCPay at work.`
            : ""}
      </Text>
      <div ref={ref}>
        {walkthrough ? (
          <MarkdownDoc markdown={walkthrough} className="or-integration-walkthrough" copyAnchors={false} />
        ) : guidesChunk.loaded && framework.video ? (
          <video
            className="or-demo-video-player"
            src={framework.video.url}
            poster={framework.video.poster ?? undefined}
            controls
            playsInline
            preload="metadata"
            aria-label={`The OpenReceive plugin connected to ${framework.label}`}
          >
            <Anchor href={framework.video.url}>Watch the demo video</Anchor>
          </video>
        ) : null}
      </div>
    </>
  )
})
